import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  Languages as LanguagesIcon,
  CheckCircle2,
  XCircle,
  Globe
} from 'lucide-react';
import PageHeader from '@components/layout/page-header';
import { useGetAdminLanguages } from '@utils/api/admin/use-get-admin-languages';
import { AdminLanguage } from '@utils/types/admin';
import StatWidget from '@components/back-office/shared/stat-widget';
import DataGridV2 from '@components/atoms/datatable/data-grid-v2';
import { useMemo } from 'react';
import { DataGridColumn } from '@components/atoms/datatable/data-grid-v2';
import { StatusBadge } from '@components/atoms/ui-kit/badge';

/**
 * On-Prem Admin Languages Page
 * Shows available languages and models for speech-to-text.
 */
export default function AdminLanguages() {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const { data, isLoading, isError } = useGetAdminLanguages();
  const languages = data?.result ?? [];

  // Calculate stats
  const totalLanguages = languages.length;
  const enabledLanguages = languages.filter(l => l.is_enabled).length;
  const asrAvailable = languages.filter(l => l.asr_alive).length;
  const punctuationAvailable = languages.filter(
    l => l.punctuation_alive
  ).length;

  // Table columns
  const columns: DataGridColumn<AdminLanguage>[] = useMemo(
    () => [
      {
        header: t('name'),
        accessorKey: 'name' as keyof AdminLanguage,
        cell: (info: { row: { original: AdminLanguage } }) => (
          <div className="flex items-center gap-2">
            <Globe
              className="h-4 w-4"
              style={{ color: getColour('inactiveText') }}
            />
            <span
              className="font-medium"
              style={{ color: getColour('normalText') }}
            >
              {info.row.original.name}
            </span>
          </div>
        )
      },
      {
        header: t('key'),
        accessorKey: 'key' as keyof AdminLanguage,
        cell: (info: { row: { original: AdminLanguage } }) => (
          <code
            className="text-xs px-1.5 py-0.5 rounded-2xl"
            style={{
              backgroundColor: getColour('thirdLayer'),
              color: getColour('normalText')
            }}
          >
            {info.row.original.key}
          </code>
        )
      },
      {
        header: t('enabled'),
        accessorKey: 'is_enabled' as keyof AdminLanguage,
        cell: (info: { row: { original: AdminLanguage } }) => (
          <StatusBadge
            status={info.row.original.is_enabled ? 'active' : 'inactive'}
            label={info.row.original.is_enabled ? t('yes') : t('no')}
          />
        )
      },
      {
        header: t('private'),
        accessorKey: 'is_private' as keyof AdminLanguage,
        cell: (info: { row: { original: AdminLanguage } }) => (
          <StatusBadge
            status={info.row.original.is_private ? 'active' : 'inactive'}
            label={info.row.original.is_private ? t('yes') : t('no')}
          />
        )
      },
      {
        header: 'ASR',
        accessorKey: 'asr_alive' as keyof AdminLanguage,
        cell: (info: { row: { original: AdminLanguage } }) => (
          <StatusBadge
            status={info.row.original.asr_alive ? 'active' : 'inactive'}
            label={
              info.row.original.asr_alive ? t('available') : t('unavailable')
            }
          />
        )
      },
      {
        header: t('punctuation'),
        accessorKey: 'punctuation_alive' as keyof AdminLanguage,
        cell: (info: { row: { original: AdminLanguage } }) => (
          <StatusBadge
            status={info.row.original.punctuation_alive ? 'active' : 'inactive'}
            label={
              info.row.original.punctuation_alive
                ? t('available')
                : t('unavailable')
            }
          />
        )
      },
      {
        header: t('deepl-code'),
        accessorKey: 'deepl_code' as keyof AdminLanguage,
        cell: (info: { row: { original: AdminLanguage } }) => (
          <span style={{ color: getColour('normalText') }}>
            {info.row.original.deepl_code || '-'}
          </span>
        )
      }
    ],
    [t, getColour]
  );

  if (isError) {
    return (
      <div className="px-4 py-2">
        <PageHeader
          title={t('languages')}
          caption={t('available-languages-and-models-for-speech-to-text')}
        />
        <div
          className="rounded-2xl border p-8 text-center"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          {}
          <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500 dark:text-red-400" />
          <p className="text-sm" style={{ color: getColour('inactiveText') }}>
            {t('failed-to-load-languages')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-app min-h-screen px-4 py-2">
      <PageHeader
        title={t('languages')}
        caption={t('available-languages-and-models-for-speech-to-text')}
      />

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatWidget
          icon={<LanguagesIcon className="h-5 w-5" />}
          title={t('total-languages')}
          value={totalLanguages}
          loading={isLoading}
        />
        <StatWidget
          icon={<CheckCircle2 className="h-5 w-5 text-green-500" />}
          title={t('enabled')}
          value={enabledLanguages}
          loading={isLoading}
        />
        <StatWidget
          icon={<Globe className="h-5 w-5 text-blue-500" />}
          title={t('asr-available')}
          value={asrAvailable}
          loading={isLoading}
        />
        <StatWidget
          icon={<CheckCircle2 className="h-5 w-5 text-purple-500" />}
          title={t('punctuation-available')}
          value={punctuationAvailable}
          loading={isLoading}
        />
      </div>

      {/* Languages Table */}
      {!isLoading && languages.length === 0 ? (
        <div
          className="rounded-2xl border p-8 text-center"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <LanguagesIcon
            className="h-12 w-12 mx-auto mb-4"
            style={{ color: getColour('inactiveText') }}
          />
          <p className="text-sm" style={{ color: getColour('inactiveText') }}>
            {t('no-languages-available')}
          </p>
        </div>
      ) : (
        <div
          className="rounded-2xl border overflow-hidden"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <DataGridV2
            data={languages}
            columns={columns}
            enableSorting={true}
            enableFiltering={true}
            height={800}
            loading={isLoading}
          />
        </div>
      )}
    </div>
  );
}
